import { AnimatePresence, motion } from 'framer-motion';
import { Check, CheckCircle2, Clock, PhoneOff } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../ui/tooltip';
import { EscalatePopover } from './EscalatePopover';
import { RejectPopover } from './RejectPopover';
import { SendRetainerButton } from './SendRetainerButton';
import { QualificationResult } from './types';
import { EndIntakeButton } from './v2/EndIntakeButton';
import {
  EndIntakeValidationResult,
  FirstContactOutcome,
  MissingField,
} from './v2/types';

interface ScreeningFooterProps {
  qualificationResult: QualificationResult;
  clientEmail: string | undefined;
  onSendRetainer: (email: string) => void;
  onEmailChange?: (email: string) => void;
  /** Callback when retainer is marked as sent manually */
  onMarkSentManually?: () => void;
  onReject: (reason: string) => void;
  onEscalate: (shouldQualify: boolean, reason: string) => void;
  /** Default reject reason to prefill in the reject popover (e.g., 'Outside Practice Area' for case type rejection) */
  defaultRejectReason?: string;
  isSaving?: boolean;
  lastSaved?: Date | null;
  // Action timestamps to show actual status vs recommendation
  retainerSentAt?: string | null;
  rejectedAt?: string | null;
  escalatedAt?: string | null;
  // V2 End Intake props
  isV2Mode?: boolean;
  endIntakeValidation?: EndIntakeValidationResult;
  onEndIntake?: (outcome: FirstContactOutcome, missingFields: MissingField[]) => void | Promise<void>;
  onNavigateToField?: (fieldId: string, screen: 1 | 2) => void;
  /** First contact outcome from End Intake */
  firstContactOutcome?: FirstContactOutcome | null;
  /** Reason for client being unavailable (shown on hover when outcome is unavailable) */
  unavailableReason?: string | null;
  /** Timestamp when End Intake was clicked */
  endIntakeAt?: string | null;
  /** Timestamp when client was called */
  calledClientAt?: string | null;
  /** Callback when "spoke with client" is toggled */
  onSpokeWithClient?: () => void;
  /** When true, programmatically opens the End Intake popover (e.g., after reject) */
  autoOpenEndIntake?: boolean;
  /** Called after the End Intake auto-open has been processed */
  onAutoOpenEndIntakeHandled?: () => void;
  /** Whether the End Intake API call is in flight (for loading indicator) */
  isEndingIntake?: boolean;
  /** Firm ID for retainer template preview */
  firmId?: string;
}

// Animated checkbox component with delightful micro-interactions
const AnimatedCheckbox: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
  tooltipText: string;
}> = ({ checked, onChange, label, tooltipText }) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.button
          type="button"
          onClick={onChange}
          className={cn(
            'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors',
            checked
              ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300'
          )}
          whileTap={{ scale: 0.97 }}
        >
          {/* Animated checkbox box */}
          <motion.div
            className={cn(
              'w-4 h-4 rounded inline-flex items-center justify-center overflow-hidden shrink-0',
              checked
                ? 'bg-emerald-500'
                : 'border-2 border-zinc-600'
            )}
            animate={{
              scale: checked ? [1, 1.2, 1] : 1,
              borderColor: checked ? 'rgb(16 185 129)' : 'rgb(82 82 91)',
            }}
            transition={{
              scale: { duration: 0.2, ease: [0.34, 1.56, 0.64, 1] },
              borderColor: { duration: 0.15 },
            }}
          >
            {/* Animated checkmark */}
            <AnimatePresence mode="wait">
              {checked && (
                <motion.svg
                  key="check"
                  viewBox="0 0 12 12"
                  className="w-2.5 h-2.5"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 25,
                    delay: 0.05,
                  }}
                >
                  <motion.path
                    d="M2 6L5 9L10 3"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{
                      duration: 0.2,
                      delay: 0.1,
                      ease: 'easeOut',
                    }}
                  />
                </motion.svg>
              )}
            </AnimatePresence>
          </motion.div>
          <span className="leading-none">{label}</span>
        </motion.button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="bg-zinc-900 border border-zinc-700 px-3 py-2"
      >
        <p className="text-xs text-zinc-300">{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export const ScreeningFooter: React.FC<ScreeningFooterProps> = ({
  qualificationResult,
  clientEmail,
  onSendRetainer,
  onEmailChange,
  onMarkSentManually,
  onReject,
  onEscalate,
  defaultRejectReason,
  isSaving = false,
  lastSaved,
  retainerSentAt,
  rejectedAt,
  escalatedAt,
  // V2 End Intake props
  isV2Mode = false,
  endIntakeValidation,
  onEndIntake,
  onNavigateToField,
  firstContactOutcome,
  unavailableReason,
  endIntakeAt,
  calledClientAt,
  onSpokeWithClient,
  autoOpenEndIntake,
  onAutoOpenEndIntakeHandled,
  isEndingIntake,
  firmId,
}) => {
  const { status } = qualificationResult;

  // Optimistic state for the "spoke with client" checkbox
  const [optimisticChecked, setOptimisticChecked] = useState(!!calledClientAt);

  // Sync optimistic state with actual prop when it changes
  useEffect(() => {
    setOptimisticChecked(!!calledClientAt);
  }, [calledClientAt]);

  // Handle checkbox toggle with optimistic update
  const handleSpokeWithClientToggle = useCallback(() => {
    // Immediately update local state (optimistic)
    setOptimisticChecked((prev) => !prev);
    // Then trigger the actual API call
    onSpokeWithClient?.();
  }, [onSpokeWithClient]);

  // Determine if an action has been taken
  const actionTaken = retainerSentAt || rejectedAt || escalatedAt;

  // Check if End Intake has been clicked
  const endIntakeCompleted = firstContactOutcome !== null && firstContactOutcome !== undefined;

  // Determine which action is recommended based on status
  const isRejectRecommended = status === 'disqualified';
  const isEscalateRecommended = status === 'escalate';
  const isSendRetainerRecommended = status === 'qualified';

  // Render the first contact outcome badge (V2 mode only)
  const renderFirstContactOutcome = () => {
    if (!isV2Mode || !endIntakeCompleted || !firstContactOutcome) return null;

    const outcomeConfig = {
      completed: {
        icon: <CheckCircle2 className="w-3 h-3" />,
        label: 'Completed',
        className: 'bg-emerald-500 text-white',
      },
      incomplete: {
        icon: <Clock className="w-3 h-3" />,
        label: 'Incomplete',
        className: 'bg-amber-500 text-white',
      },
      unavailable: {
        icon: <PhoneOff className="w-3 h-3" />,
        label: 'Unavailable',
        className: 'bg-zinc-500 text-white',
      },
    };

    const config = outcomeConfig[firstContactOutcome];
    const formattedTime = endIntakeAt
      ? new Date(endIntakeAt).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      : null;

    const badge = (
      <div
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider cursor-help',
          config.className
        )}
      >
        {config.icon}
        {config.label}
      </div>
    );

    // Always wrap in tooltip to show timestamp (and reason for unavailable)
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-zinc-900 border border-zinc-700 px-4 py-3 max-w-xs"
        >
          <div className="space-y-2">
            {formattedTime && (
              <p className="text-xs text-zinc-300">
                Marked {formattedTime}
              </p>
            )}
            {firstContactOutcome === 'unavailable' && unavailableReason && (
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                  Reason
                </p>
                <p className="text-sm font-medium text-white leading-relaxed">
                  {unavailableReason}
                </p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Render the status indicator (only shows actual status, not recommendations)
  const renderStatusIndicator = () => {
    // Only show badges for actual actions taken
    if (retainerSentAt) {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500 text-white">
          <Check className="w-3 h-3" />
          Retainer Sent
        </div>
      );
    }
    if (rejectedAt) {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-500 text-white">
          <Check className="w-3 h-3" />
          Rejected
        </div>
      );
    }
    if (escalatedAt) {
      return (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-400 text-zinc-900">
          <Check className="w-3 h-3" />
          Escalated
        </div>
      );
    }

    // No action taken - don't show any badge (action buttons already indicate recommendations)
    return null;
  };

  return (
    <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-900">
      {/* Left side: Status indicator + first contact outcome + auto-save */}
      <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
        {/* Status pill */}
        {renderStatusIndicator()}

        {/* First contact outcome badge (V2 mode only) */}
        {renderFirstContactOutcome()}

        {/* Minimal auto-save indicator */}
        <div
          className="flex items-center gap-1.5"
          title={(() => {
            if (isSaving) return 'Saving...';
            if (lastSaved) return 'Auto-saved';
            return '';
          })()}
        >
          <div
            className={cn(
              'w-1.5 h-1.5 rounded-full transition-colors',
              isSaving && 'bg-zinc-500',
              !isSaving && lastSaved && 'bg-emerald-500',
              !isSaving && !lastSaved && 'bg-zinc-700',
            )}
          />
          {isSaving && (
            <span className="text-[9px] font-medium text-zinc-500">Saving</span>
          )}
        </div>
      </div>

      {/* Center: Spoke with client toggle (V2 mode only) */}
      {isV2Mode && onSpokeWithClient && (
        <AnimatedCheckbox
          checked={optimisticChecked}
          onChange={handleSpokeWithClientToggle}
          label="Spoke with client"
          tooltipText={
            calledClientAt
              ? `Logged ${new Date(calledClientAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}`
              : 'Click to log that you spoke with the client'
          }
        />
      )}

      {/* Right side: Action buttons - Cash App pill style */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* End Intake button - V2 mode only, still clickable after intake is ended but with subdued styling */}
        {isV2Mode && endIntakeValidation && onEndIntake && onNavigateToField && (
          <EndIntakeButton
            validation={endIntakeValidation}
            onEndIntake={onEndIntake}
            onNavigateToField={onNavigateToField}
            hasBeenClicked={endIntakeCompleted}
            autoOpen={autoOpenEndIntake}
            onAutoOpenHandled={onAutoOpenEndIntakeHandled}
            isEndingIntake={isEndingIntake}
          />
        )}

        {/* Reject popover */}
        <RejectPopover
          onReject={onReject}
          isRecommended={isRejectRecommended}
          disabled={!!actionTaken}
          defaultReason={defaultRejectReason}
        />

        {/* Escalate button with popover */}
        <EscalatePopover
          onEscalate={onEscalate}
          isRecommended={isEscalateRecommended}
          disabled={!!actionTaken}
        />

        {/* Send Retainer button - still clickable when escalated */}
        <SendRetainerButton
          status={status}
          clientEmail={clientEmail}
          onSendRetainer={onSendRetainer}
          onEmailChange={onEmailChange}
          onMarkSentManually={onMarkSentManually}
          isRecommended={isSendRetainerRecommended}
          disabled={!!(retainerSentAt || rejectedAt)}
          firmId={firmId}
        />
      </div>
    </div>
  );
};
